import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Button,
  Drawer,
  Input,
  ListBox,
  Select,
  Switch,
  TextArea,
  TextField,
} from "@heroui/react";
import { helpSettingsStore } from "@/stores/HelpSettingsStore";
import {
  HELP_PRICING_MODE_LABEL,
  HELP_PRICING_MODE_OPTIONS,
} from "@/pages/HelpPage/constants";
import type { HelpPricingMode } from "@/pages/HelpPage/types";

const ServiceFormDrawer: React.FC = observer(() => {
  const store = helpSettingsStore;
  const {
    serviceFormOpen,
    isServiceEditing,
    serviceEditingUuid,
    serviceForm,
    serviceFormErrors,
    serviceSubmitAttempted,
    serviceSubmitting,
  } = store;

  const fieldError = (field: string) =>
    serviceSubmitAttempted ? serviceFormErrors[field] : undefined;

  const [priceText, setPriceText] = useState("");

  useEffect(() => {
    if (!serviceFormOpen) return;
    setPriceText(
      serviceForm.unitPrice > 0 ? String(serviceForm.unitPrice) : "",
    );
  }, [serviceFormOpen, isServiceEditing, serviceEditingUuid]);

  const syncPriceToStore = (raw: string) => {
    const trimmed = raw.trim();
    if (trimmed === "") {
      store.setServiceField("unitPrice", 0);
      return;
    }
    const parsed = Number(trimmed);
    if (!Number.isNaN(parsed)) {
      store.setServiceField("unitPrice", Math.max(0, parsed));
    }
  };

  return (
    <Drawer.Backdrop
      isOpen={serviceFormOpen}
      onOpenChange={(open) => {
        if (!open) store.requestCloseServiceForm();
      }}
      className="help-view-backdrop"
    >
      <Drawer.Content placement="right" className="help-view-content">
        <Drawer.Dialog className="help-view-dialog" data-testid="help-settings-service-drawer">
          <Drawer.CloseTrigger data-testid="help-settings-service-drawer-close" />
          <Drawer.Header className="help-view-header">
            <Drawer.Heading>
              {isServiceEditing ? "Редактирование услуги" : "Новая услуга"}
            </Drawer.Heading>
            <p className="help-view-subtitle">
              Появится в каталоге для добавления в чек заявки
            </p>
          </Drawer.Header>

          <Drawer.Body className="help-view-body help-settings-drawer-body">
            <div className="help-settings-form">
              <div className="help-settings-form-row help-settings-form-row--title-desc">
                <div className="help-form-field help-settings-field--w242 help-settings-input--h36">
                  <span className="help-form-field__label">Название *</span>
                  <TextField fullWidth isInvalid={!!fieldError("title")}>
                    <Input
                      value={serviceForm.title}
                      onChange={(e) =>
                        store.setServiceField("title", e.target.value)
                      }
                      placeholder="Сложный подъезд"
                    />
                  </TextField>
                  {fieldError("title") ? (
                    <span className="help-form-field__error">
                      {fieldError("title")}
                    </span>
                  ) : null}
                </div>

                <div className="help-form-field help-settings-field--w242 help-settings-textarea--h76 help-form-field--description">
                  <span className="help-form-field__label">Описание</span>
                  <TextArea
                    fullWidth
                    rows={3}
                    value={serviceForm.description}
                    onChange={(e) =>
                      store.setServiceField("description", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="help-settings-form-row help-settings-form-row--pricing">
                <div className="help-form-field">
                  <span className="help-form-field__label">Цена, ₽ *</span>
                  <TextField fullWidth isInvalid={!!fieldError("unitPrice")}>
                    <Input
                      inputMode="numeric"
                      value={priceText}
                      onChange={(e) => {
                        const next = e.target.value.replace(/[^\d]/g, "");
                        setPriceText(next);
                        if (next === "") {
                          store.setServiceField("unitPrice", 0);
                          return;
                        }
                        const parsed = Number(next);
                        if (!Number.isNaN(parsed)) {
                          store.setServiceField("unitPrice", Math.max(0, parsed));
                        }
                      }}
                      onBlur={() => syncPriceToStore(priceText)}
                    />
                  </TextField>
                  {fieldError("unitPrice") ? (
                    <span className="help-form-field__error">
                      {fieldError("unitPrice")}
                    </span>
                  ) : null}
                </div>

                <div className="help-form-field">
                  <span className="help-form-field__label">Единица</span>
                  <TextField fullWidth>
                    <Input
                      value={serviceForm.unitLabel}
                      onChange={(e) =>
                        store.setServiceField("unitLabel", e.target.value)
                      }
                      placeholder="колесо, км, заказ"
                    />
                  </TextField>
                </div>

                <div className="help-form-field">
                  <span className="help-form-field__label">Тип цены</span>
                  <Select
                    selectedKey={serviceForm.pricingMode}
                    onSelectionChange={(key) => {
                      if (!key) return;
                      store.setServiceField(
                        "pricingMode",
                        String(key) as HelpPricingMode,
                      );
                    }}
                    aria-label="Тип цены"
                    isInvalid={!!fieldError("pricingMode")}
                  >
                    <Select.Trigger>
                      <Select.Value />
                      <Select.Indicator />
                    </Select.Trigger>
                    <Select.Popover>
                      <ListBox>
                        {HELP_PRICING_MODE_OPTIONS.map((mode) => (
                          <ListBox.Item
                            key={mode}
                            id={mode}
                            textValue={HELP_PRICING_MODE_LABEL[mode]}
                          >
                            {mode === "fixed"
                              ? "Фикс — одна позиция"
                              : mode === "per_unit"
                                ? "За единицу — количество в заявке"
                                : "Гибкая — цену правим в заявке"}
                          </ListBox.Item>
                        ))}
                      </ListBox>
                    </Select.Popover>
                  </Select>
                  {fieldError("pricingMode") ? (
                    <span className="help-form-field__error">
                      {fieldError("pricingMode")}
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="help-form-field help-settings-field--w242 help-settings-input--h36">
                <span className="help-form-field__label">LAT service_id</span>
                <TextField fullWidth>
                  <Input
                    value={serviceForm.providerServiceId}
                    onChange={(e) =>
                      store.setServiceField("providerServiceId", e.target.value)
                    }
                    placeholder="необязательно"
                  />
                </TextField>
              </div>

              <div className="help-settings-switches">
              <label className="help-switch-row help-settings-switch-row">
                В каталоге заявок
                <Switch
                  className="fbs-switch"
                  isSelected={serviceForm.enabled}
                  onChange={(checked) =>
                    store.setServiceField("enabled", checked)
                  }
                >
                  <Switch.Control>
                    <Switch.Thumb />
                  </Switch.Control>
                </Switch>
              </label>

              <label className="help-switch-row help-settings-switch-row">
                Скоро в приложении
                <Switch
                  className="fbs-switch"
                  isSelected={serviceForm.comingSoon}
                  onChange={(checked) =>
                    store.setServiceField("comingSoon", checked)
                  }
                >
                  <Switch.Control>
                    <Switch.Thumb />
                  </Switch.Control>
                </Switch>
              </label>
              </div>
            </div>
          </Drawer.Body>

          <Drawer.Footer className="help-settings-footer">
            <Button
              variant="tertiary"
              onPress={() => store.requestCloseServiceForm()}
              data-testid="help-settings-service-cancel"
            >
              Отмена
            </Button>
            <Button
              variant="primary"
              onPress={() => void store.submitService()}
              isPending={serviceSubmitting}
              data-testid="help-settings-service-submit"
            >
              Сохранить
            </Button>
          </Drawer.Footer>
        </Drawer.Dialog>
      </Drawer.Content>
    </Drawer.Backdrop>
  );
});

ServiceFormDrawer.displayName = "ServiceFormDrawer";
export default ServiceFormDrawer;
